import { MILLIS_IN_SECOND, MINUTES_IN_HOUR, SECONDS_IN_HOUR, SECONDS_IN_MINUTE } from '@/constants/global';
import { LUXON_DATE_FORMAT_LONG, TIME_FORMAT_IN_MINUTES_FULL, TIME_FORMAT_LONG } from '@/constants/dateTimeFormats';
import { round } from '@/util/numberFormatter';
import { DateTime, Duration } from 'luxon';

export function duration(timeInSeconds) {
    timeInSeconds = Number(timeInSeconds);
    const hours = Math.floor(timeInSeconds / SECONDS_IN_HOUR);
    const minutes = Math.floor((timeInSeconds % SECONDS_IN_HOUR) / MINUTES_IN_HOUR);
    const seconds = Math.floor((timeInSeconds % SECONDS_IN_HOUR) % SECONDS_IN_MINUTE);
    return { hours, minutes, seconds };
}

export function humanizeSecondsToHms(timeInSeconds, emptyValue = '') {
    if (!timeInSeconds) return emptyValue;
    const { hours, minutes, seconds } = duration(timeInSeconds);

    const formattedHours = hours ? `${hours}h` : '';
    const formattedMinutes = minutes ? `${minutes}min` : '';
    const formattedSeconds = seconds ? `${seconds}sec` : '';

    return `${formattedHours} ${formattedMinutes} ${formattedSeconds}`.trim();
}

export function secondsToHms(timeInSeconds, emptyValue = '') {
    if (!timeInSeconds) return emptyValue;
    const { hours, minutes, seconds } = duration(timeInSeconds);
    return formatTimeNumber(hours) + ':' + formatTimeNumber(minutes) + ':' + formatTimeNumber(seconds);
}

export function formatTimeNumber(time) {
    return time < 10 ? '0' + time : time;
}

/**
 * Format incomplete timestring to match the placeholder pattern. Returns string
 * padded with zeros which conforms to the placeholder pattern
 * @param {String} value String representation of time
 * @param {String} placeholder A placeholder to combine with the value
 *
 * @return {String}
 */
export function formatInputTimeValue(value, placeholder) {
    if (!placeholder) {
        console.error('Missing placeholder parameter in formatInputTimeValue');
    }
    if (!value) return placeholder;
    return value + placeholder.substring(value.toString().length, placeholder.length);
}

function formatMillis(value, format, placeholder = '000') {
    if (!value) return placeholder;

    const array = format.split('.');
    if (!array.length || array.length === 1) return value;
    const afterDot = array[1] && array[1].length;

    const placeholderPart = placeholder.substring(value.toString().length, placeholder.length);
    return afterDot === 1 ? value + placeholderPart : placeholderPart + value;
}

/**
 *
 * @param {String} val time string to convert to seconds, eg 1:23:45.6
 * @param {String} format any time format supported by Luxon
 * @return {Number|null} returns number of seconds in val or null if val is invalid
 */
export function timeStringToSeconds(val, format) {
    if (!format) {
        console.error('Missing format parameter in timeStringToSeconds');
    }
    const time = DateTime.fromFormat(val, format);
    if (!time.isValid) return null;

    const withoutHours = !format.includes('H');
    const { hour, minute, second, millisecond } = time;
    const duration = Duration.fromObject({
        hours: withoutHours ? 0 : hour,
        minutes: minute || 0,
        seconds: second || 0
    }).as('seconds');
    const millis = Number(formatMillis(millisecond, format));
    return millisecond ? duration + millis / 1000 : duration;
}

export function toUnixMillis(date) {
    return date ? DateTime.fromSQL(date).valueOf() : 0;
}

export function formatDateTimeFromMillis(millis, format = LUXON_DATE_FORMAT_LONG, emptyValue = '-') {
    return !millis ? emptyValue : DateTime.fromMillis(millis).toFormat(format);
}

export function formatDateTimeFromSQL(sqlDate, format = LUXON_DATE_FORMAT_LONG, emptyValue = '-') {
    return !sqlDate ? emptyValue : DateTime.fromSQL(sqlDate).toFormat(format);
}

export function formatDateTimeFromISO(isoDate, format, emptyValue = '-') {
    if (!isoDate) return emptyValue;
    return format ? DateTime.fromISO(isoDate).toFormat(format) : DateTime.fromISO(isoDate).toISODate();
}

export function formatGpsTimeOrTime(time, type = 'time', format = null, emptyValue = '') {
    if (type === 'time') return formatTime(time, format, emptyValue);

    const digits = Math.round(time).toString().length;
    const timeInMillis = digits === 10 ? time * MILLIS_IN_SECOND : time;
    return (
        DateTime.fromMillis(timeInMillis)
            .toUTC()
            .toFormat(format || TIME_FORMAT_LONG) || emptyValue
    );
}

export function formatTime(seconds, format = null, emptyValue = '', decimals = 1) {
    if (!seconds) return emptyValue;

    const timeFormat = format ? format : seconds >= SECONDS_IN_HOUR ? TIME_FORMAT_LONG : TIME_FORMAT_IN_MINUTES_FULL;
    const formattedTime = DateTime.now()
        .startOf('day')
        .plus({ seconds: round(seconds, decimals) })
        .toFormat(timeFormat);
    return cutDecimals(formattedTime, decimals);
}

function cutDecimals(formattedTime = '', decimals = 1) {
    const array = formattedTime.split('.');
    if (!array.length || array.length === 1) return formattedTime;
    const afterDot = array[1] && array[1].length;
    return afterDot === decimals
        ? formattedTime
        : formattedTime.substring(0, formattedTime.length - (afterDot - decimals));
}
